import React from 'react';
import { Check, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import {
  PgHero, PgHeroInner, PgTitle, PgIn, PgPill, PgSurface,
  PgSplit, PgSplitText, PgSplitPanel, PgFeats, PgMini,
  PgOutro, PgOutroItem,
} from './product';
import { scrollToId } from '../lib/scroll';

const surfaceStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 1.05 } },
};
const surfaceItem = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 140, damping: 16 } },
};

const documents = [
  { label: 'Plicuri', n: '24' },
  { label: 'Notificări', n: '24' },
  { label: 'Confirmări de primire', n: '24' },
  { label: 'Borderou de expediere', n: '1' },
];

export const CorespondentaPage = ({ onNavigate }) => {
  const handleContact = (e) => {
    e.preventDefault();
    if (onNavigate) {
      onNavigate('home');
      setTimeout(() => scrollToId('#contact'), 120);
    }
  };

  return (
    <>
      {/* Hero */}
      <PgHero>
        <PgHeroInner>
          <PgTitle
            lines={[
              'De la sentință la plicuri,',
              <span className="accent">fără munca de secretariat.</span>,
            ]}
          />
          <PgIn delay={0.65}>
            <p className="pg-lead">
              Încarci sentința și lista de creditori. Plicuri pregătește întreaga corespondență —
              plicuri, notificări, confirmări de primire și borderou — cu datele completate. Tu verifici și tipărești.
            </p>
          </PgIn>
          <PgIn delay={0.78} className="pg-cta">
            <PgPill href="/#contact" onClick={handleContact}>
              Înscrie-te pe lista de așteptare <ArrowRight size={17} />
            </PgPill>
            <a href="#detalii" className="pg-ghost">Vezi cum arată</a>
          </PgIn>
        </PgHeroInner>

        {/* Bespoke product surface — real content, no window chrome */}
        <PgSurface id="detalii">
          <div className="pg-surface-head">
            <span className="pg-surface-title">Dosar 6789/118/2026</span>
            <span className="pg-surface-meta">Tribunalul Constanța · Legea 85/2014</span>
          </div>
          <motion.div
            className="pg-surface-body"
            initial="hidden"
            animate="visible"
            variants={surfaceStagger}
          >
            <motion.div className="pg-fieldset" variants={surfaceItem}>
              <div className="pg-field"><span className="k">Debitor</span><span className="v">Novaria Construct S.R.L.</span></div>
              <div className="pg-field"><span className="k">CUI</span><span className="v">RO 12345678</span></div>
              <div className="pg-field"><span className="k">Termen depunere creanțe</span><span className="v">30.04.2026</span></div>
              <div className="pg-field"><span className="k">Creditori</span><span className="v">24</span></div>
            </motion.div>

            <motion.div
              variants={{
                hidden: { opacity: 0, x: -10 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.5 } },
              }}
              style={{ display: 'flex' }}
            >
              <ArrowRight className="pg-arrow" size={22} />
            </motion.div>

            <motion.div
              className="pg-doclist"
              variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.12 } } }}
            >
              {documents.map((d) => (
                <motion.div key={d.label} className="pg-doc" variants={surfaceItem}>
                  <Check className="tick" size={15} />
                  {d.label}
                  <span className="n">{d.n}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </PgSurface>
      </PgHero>

      {/* Split 1 — data from official sources */}
      <section className="section">
        <div className="container">
          <PgSplit>
            <PgSplitText>
              <h2 className="pg-split-h">Datele se completează singure, <span className="accent">din surse oficiale.</span></h2>
              <p className="pg-split-p">
                Numărul dosarului, debitorul și termenele sunt citite din sentință de un model AI procesat
                în Uniunea Europeană. Adresele creditorilor vin din ANAF, iar instituțiile uzuale se adaugă
                ca destinatari. Pentru debitori străini sau fără TVA, adresa e căutată și în VIES și în
                registrele publice.
              </p>
              <PgFeats items={[
                'Extragere din sentință, cu diacriticele corecte',
                'Adrese din ANAF după CUI, sau din VIES pentru firme străine',
                'ANAF, AAAS, primăriile și DGITL adăugate automat',
              ]} />
            </PgSplitText>
            <PgSplitPanel>
              <PgMini
                label="Extras din sentință"
                rows={[
                  { k: 'Dosar', v: '6789/118/2026' },
                  { k: 'Debitor', v: 'Novaria Construct' },
                  { k: 'Instanță', v: 'Trib. Constanța' },
                  { k: 'Termen creanțe', v: '30.04.2026', hi: true },
                ]}
              />
            </PgSplitPanel>
          </PgSplit>
        </div>
      </section>

      {/* Split 2 — guided wizard */}
      <section className="section">
        <div className="container">
          <PgSplit reverse>
            <PgSplitPanel>
              <PgMini
                label="Corespondența ghidată"
                rows={[
                  { k: '1 · Expeditor', v: 'cabinet' },
                  { k: '2 · Destinatari', v: '24' },
                  { k: '3 · Documente', v: 'alese' },
                  { k: '4 · Verifică', v: 'previzualizare', hi: true },
                ]}
              />
            </PgSplitPanel>
            <PgSplitText>
              <h2 className="pg-split-h">Un dosar procesat, <span className="accent">tot setul de documente.</span></h2>
              <p className="pg-split-p">
                Un asistent te duce pas cu pas — expeditor, destinatari, documente, verificare — cu
                previzualizare live la plic și notificare. Reiei un dosar deja procesat fără să plătești
                din nou, editezi datele pe loc și tipărești când totul e corect.
              </p>
              <PgFeats items={[
                'Asistent în patru pași, cu previzualizare live',
                'Reutilizezi un dosar procesat, fără cost în plus',
                'Notificări pentru creditori și pentru debitor',
              ]} />
            </PgSplitText>
          </PgSplit>
        </div>
      </section>

      {/* Split 3 — documents, templates, team, cross-app */}
      <section className="section">
        <div className="container">
          <PgSplit>
            <PgSplitText>
              <h2 className="pg-split-h">Redactezi documente <span className="accent">cu asistență, nu de la zero.</span></h2>
              <p className="pg-split-p">
                Pe lângă corespondența standard, redactezi adrese pornind de la datele dosarului și de la
                propriile modele de stil, cu previzualizare A4 și export în DOCX sau PDF. Dosarele stau în
                cloud, izolate pe organizația ta, iar termenele dintr-o sentință procesată aici ajung
                automat în calendarul din Borderou.
              </p>
              <PgFeats items={[
                'Adrese în stilul tău, export DOCX sau PDF',
                'Șabloane de notificare editabile, per organizație',
                'Workspace cu roluri; termenele merg singure în Borderou',
              ]} />
            </PgSplitText>
            <PgSplitPanel>
              <PgMini label="Documente">
                <div className="pg-doc"><Check className="tick" size={15} />Adresă în stilul cabinetului</div>
                <div className="pg-doc"><Check className="tick" size={15} />Notificare din șablonul tău</div>
                <div className="pg-doc"><Check className="tick" size={15} />Termene trimise în Borderou</div>
              </PgMini>
            </PgSplitPanel>
          </PgSplit>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container">
          <PgOutro>
            <PgOutroItem as="h2" className="pg-h1" style={{ fontSize: 'clamp(2rem, 4.5vw, 3.25rem)', marginBottom: '1rem' }}>
              Plicuri este <span className="accent">în curs de lansare.</span>
            </PgOutroItem>
            <PgOutroItem as="p" className="pg-lead" style={{ margin: '0 auto 2.25rem' }}>
              Plătești pe măsură ce trimiți — credite, fără abonament. Înscrie-te pe lista de așteptare
              și te anunțăm când devine disponibilă.
            </PgOutroItem>
            <PgOutroItem className="pg-cta" style={{ justifyContent: 'center' }}>
              <PgPill href="/#contact" onClick={handleContact}>
                Înscrie-te pe lista de așteptare <ArrowRight size={17} />
              </PgPill>
            </PgOutroItem>
          </PgOutro>
        </div>
      </section>
    </>
  );
};

export default CorespondentaPage;
